import { useMemo } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { uploadsUrl } from "../utils/api";
import "./Hero.css";

function extractYoutubeId(url) {
  if (!url) return null;

  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:shorts\/|watch\?v=|embed\/))([^?&/]+)/
  );

  return m ? m[1] : null;
}

function withStartingPrefix(price) {
  if (!price) return "";

  return /^starting\b/i.test(price.trim())
    ? price.trim()
    : `Starting ${price}`;
}

export default function Hero({
  projectId,
  header,
  developers = [],
  onCtaClick,
}) {
  const {
    projectName,
    developerName,
    projectSubheading,
    startingPrice,
    siteVisitText,
    youtubeVideoUrl,
    mobileReelUrl,
    projectNameAgainstSiteName,
  } = header;

  const desktopDeveloperName = developers?.[0]?.title || developerName;

  const mobileId = useMemo(
    () => extractYoutubeId(mobileReelUrl),
    [mobileReelUrl]
  );

  const desktopId = useMemo(
    () => extractYoutubeId(youtubeVideoUrl),
    [youtubeVideoUrl]
  );

  const hasMedia = mobileId || desktopId;

  const bgStyle = !hasMedia
    ? {
        "--bg1": `url('${uploadsUrl(projectId, "background1.webp")}')`,
        "--bg2": `url('${uploadsUrl(projectId, "background2.webp")}')`,
        "--bg3": `url('${uploadsUrl(projectId, "background3.webp")}')`,
      }
    : {};

  return (
    <div className="hero-wrap" id="home">
      <header
        className={`hero-header${!hasMedia ? " no-media" : ""}`}
        style={bgStyle}
        aria-label="Hero section"
      >
        {/* Background media */}
        {hasMedia && (
          <div className="hero-media">
            {mobileId && (
              <iframe
                className="hero-media__iframe hero-media__iframe--mobile"
                src={`https://www.youtube.com/embed/${mobileId}?autoplay=1&mute=1&loop=1&controls=0&playlist=${mobileId}&modestbranding=1&showinfo=0&rel=0`}
                allow="autoplay; fullscreen"
                title="Background reel"
              />
            )}

            {desktopId && (
              <iframe
                className="hero-media__iframe hero-media__iframe--desktop"
                src={`https://www.youtube.com/embed/${desktopId}?autoplay=1&mute=1&loop=1&controls=0&playlist=${desktopId}&modestbranding=1&showinfo=0&rel=0`}
                allow="autoplay; fullscreen"
                title="Background video"
              />
            )}
          </div>
        )}

        <div className="hero-overlay" aria-hidden="true" />

        {/* Mobile overlay */}
        <div className="hero-mobile-overlay">
          <div className="hero-mobile-overlay__row">
            <h2 className="hero-mobile-overlay__name">
              {projectName}
            </h2>

            {startingPrice && (
              <span className="hero-mobile-price-badge">
                {withStartingPrefix(startingPrice)}
              </span>
            )}
          </div>

          {projectNameAgainstSiteName && (
            <p className="hero-mobile-overlay__location">
              <FaMapMarkerAlt aria-hidden="true" />
              {developerName}
            </p>
          )}
        </div>
      </header>

      {/* Desktop booking card */}
      <div className="hero-card">
        <div className="hero-card__badge">
          Booking Open
        </div>

        <div className="hero-card__id">
          {/* Main page heading - ONLY H1 */}
          <h1 className="hero-card__name">
            {projectName}
          </h1>

          {desktopDeveloperName && (
            <p className="hero-card__developer">
              By {desktopDeveloperName}
            </p>
          )}

          {projectNameAgainstSiteName && (
            <p className="hero-card__location">
              <FaMapMarkerAlt aria-hidden="true" />
              {developerName}
            </p>
          )}
        </div>

        {/* Subheading / offer */}
        {projectSubheading && (
          <div className="hero-card__offers">
            <div className="hero-card__offer-row">
              <span
                className="hero-card__diamond"
                aria-hidden="true"
              >
                ◆
              </span>

              {projectSubheading}
            </div>
          </div>
        )}

        {/* Price + CTA */}
        <div className="hero-card__price-block">
          {startingPrice && (
            <button
              className="hero-card__price"
              onClick={() =>
                document
                  .querySelector(".apartment-section")
                  ?.scrollIntoView({
                    behavior: "smooth",
                  })
              }
            >
              {startingPrice}{" "}
              <span className="hero-card__price-suffix">
                Onwards
              </span>
            </button>
          )}

          <button
            className="hero-card__cta"
            onClick={() => onCtaClick("Site Visit")}
          >
            {siteVisitText || "Enquire Now"}
          </button>
        </div>
      </div>

      {/* Mobile CTA */}
      <div className="hero-mobile-strip">
        <button
          className="hero-mobile-strip__cta"
          onClick={() => onCtaClick("Site Visit")}
        >
          {siteVisitText || "Book A Free Site Visit"}
        </button>
      </div>
    </div>
  );
}
